import Holidays from 'date-holidays';
import type { HolidaysTypes } from 'date-holidays';
import { DateTime } from 'luxon';

// Designated holiday list: https://www.gov.mb.ca/health/claims/providers.html
const manitobaHolidayRules: Readonly<
  Record<string, HolidaysTypes.HolidayOptions>
> = {
  'easter 1': { name: 'Easter Monday', type: 'observance' },
  '1st monday in August': { name: 'Terry Fox Day', type: 'observance' },
  '09-30 since 2021': {
    name: 'National Day for Truth and Reconciliation',
    type: 'observance',
  },
  '11-11': { name: 'Remembrance Day', type: 'observance' },
  '12-26 if saturday,sunday then next monday': {
    name: 'Boxing Day',
    type: 'observance',
  },
  '12-25 if saturday then next monday if sunday then next tuesday': {
    name: 'Christmas Day',
    type: 'public',
  },
  '01-01 if saturday,sunday then next monday': {
    name: "New Year's Day",
    type: 'public',
  },
  '07-01 if saturday,sunday then next monday': {
    name: 'Canada Day',
    type: 'public',
  },
  '09-30 if saturday,sunday then next monday since 2021': {
    name: 'National Day for Truth and Reconciliation',
    type: 'public',
  },
  '11-11 if saturday,sunday then next monday': {
    name: 'Remembrance Day',
    type: 'public',
  },
};

const holidayDatesByProvinceAndYear = new Map<string, readonly string[]>();

const createProvinceCalendar = (province: string): Holidays => {
  const calendar = new Holidays();
  if (!calendar.getStates('CA')[province]) {
    throw new Error(`Unsupported Canadian province: ${province}`);
  }
  calendar.init('CA', province);

  if (province === 'MB') {
    for (const [rule, options] of Object.entries(manitobaHolidayRules)) {
      calendar.setHoliday(rule, options);
    }
  }
  return calendar;
};

const getHolidayDates = (year: number, province: string): readonly string[] => {
  const cacheKey = `${province}:${year}`;
  const cachedDates = holidayDatesByProvinceAndYear.get(cacheKey);
  if (cachedDates) return cachedDates;

  const holidayDates = createProvinceCalendar(province)
    .getHolidays(year)
    .filter(
      (holiday) =>
        holiday.type === 'public' ||
        (province === 'MB' && Object.hasOwn(manitobaHolidayRules, holiday.rule))
    )
    .map((holiday) => holiday.date.slice(0, 10));

  const uniqueDates = [...new Set(holidayDates)];
  holidayDatesByProvinceAndYear.set(cacheKey, uniqueDates);
  return uniqueDates;
};

export const getCutoffHolidays = (
  reference: DateTime,
  province = 'MB'
): DateTime[] => {
  // Adjacent years cover cutoff windows and rollback across New Year's Day.
  return [reference.year - 1, reference.year, reference.year + 1].flatMap(
    (year) =>
      getHolidayDates(year, province).map((date) =>
        DateTime.fromISO(date, { zone: reference.zone })
      )
  );
};
